import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroller';
import { toast } from 'material-react-toastify';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { ProductSummary } from '../../interfaces/Product/ProductSummary';
import {
  getDefaultProductFilters,
  GetProductsRequest,
  ProductOrder,
} from '../../interfaces/Product/GetProductsRequest';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getProductsByCategory } from '../../api/Product';
import { SortProductsHeader } from '../../components/SortProductsHeader/SortProductsHeader';
import { ProductGrid } from '../../components/ProductGrid/ProductGrid';
import { FilterSidebar } from './FilterSidebar';
import { FilterMenuMobile } from './FilterMenuMobile';
import { getEmptyMeta } from '../../interfaces/Meta/Meta';
import { getCategoryMeta } from '../../api/Category';
import { BreadcrumbElement } from '../../interfaces/Breadcrumb/BreadcrumbElement';
import { GenericHelmet } from '../../components/Helmet/GenericHelmet';
import { GenericBreadcrumbs } from '../../components/Breadcrumbs/GenericBreadcrumbs';
import { PopupSlot } from '../../components/Popup/PopupSlot';

export const CategoryProducts = () => {
  const { slug } = useParams<string>();

  const [products, setProducts] = useState<PaginatedResponse<ProductSummary>>({
    data: [],
    totalCount: 0,
  });
  const [meta, setMeta] = useState(getEmptyMeta());
  const [order, setOrder] = useState<ProductOrder>('orderByQuantityDesc');
  const [filterIDs, setFilterIDs] = useState<number[]>([]);
  const [page, setPage] = useState(0);

  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);

  const isMobile = useIsMobile();

  const fetchProducts = useCallback(
    (filters: GetProductsRequest, replace: boolean) => {
      setLoading(true);
      getProductsByCategory(slug ?? '', filters)
        .then(
          replace
            ? setProducts
            : (nextProducts) => {
                setProducts((prev) => ({
                  data: [...prev.data, ...nextProducts.data],
                  totalCount: nextProducts.totalCount,
                }));
              },
        )
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    },
    [slug],
  );

  useEffect(() => {
    if (!slug) return;

    fetchProducts(
      {
        ...getDefaultProductFilters(),
        order,
        page: 0,
      },
      true,
    );

    setPage(0);
    setFilterIDs([]);

    getCategoryMeta(slug)
      .then((m) => {
        setMeta({
          ...m,
          title: m.title || 'Ürünler',
          description: m.description || `${slug} kategorisine ait ürünler`,
          canonicalURL: m.canonicalURL || `kategori/${slug}`,
        });
      })
      .catch((err) => toast.error(err.message));
  }, [slug]);

  const incrementPage = useCallback(() => {
    if (loading || products.data.length >= products.totalCount) return;

    const nextPage = page + 1;

    fetchProducts(
      {
        ...getDefaultProductFilters(),
        order,
        filterIDs,
        page: nextPage,
      },
      false,
    );

    setPage(nextPage);
  }, [loading, page, slug, products, filterIDs, fetchProducts]);

  const handleSort = (newOrder: ProductOrder) => {
    setOrder(newOrder);
    setPage(0);
    fetchProducts(
      { ...getDefaultProductFilters(), order: newOrder, page: 0, filterIDs },
      true,
    );
  };

  const handleFilter = (newFilterIDs: number[]) => {
    setPage(0);
    setFilterIDs(newFilterIDs);
    fetchProducts(
      {
        ...getDefaultProductFilters(),
        order,
        page: 0,
        filterIDs: newFilterIDs,
      },
      true,
    );
  };

  const resolvedLoading = loading && !products.data.length;

  const breadcrumbElemets: BreadcrumbElement[] = [
    { name: meta.title || 'Ürünler', url: `kategori/${slug}` },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        gap: 5,
        px: isMobile ? 1.5 : 2.5,
        py: 2.5,
        boxSizing: 'border-box',
      }}
    >
      <GenericHelmet
        title='Ürünler'
        meta={meta}
        breadcrumb={breadcrumbElemets}
      />

      <PopupSlot categorySlug={slug} />

      {showFilter ? (
        <FilterMenuMobile
          categorySlug={slug ?? ''}
          filterIDs={filterIDs}
          onChange={handleFilter}
          onClose={() => setShowFilter(false)}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'start',
            gap: 2.5,
            width: '100%',
          }}
        >
          {!isMobile && (
            <FilterSidebar
              categorySlug={slug ?? ''}
              filterIDs={filterIDs}
              onChange={handleFilter}
            />
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 4.5,
              alignItems: 'center',
              boxSizing: 'border-box',
            }}
          >
            <GenericBreadcrumbs
              breadcrumbElements={breadcrumbElemets}
              sx={{ mb: -3 }}
            />

            <SortProductsHeader
              loading={resolvedLoading}
              totalCount={products.totalCount}
              order={order}
              onChangeOrder={handleSort}
              filterCount={filterIDs.length}
              onFilter={() => setShowFilter(true)}
            />

            <InfiniteScroll
              pageStart={0}
              loadMore={incrementPage}
              hasMore={products.totalCount > products.data.length}
              loader={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <CircularProgress color='inherit' />
                </Box>
              }
              style={{ width: '100%' }}
            >
              <ProductGrid products={products.data} loading={resolvedLoading} />
            </InfiniteScroll>
          </Box>
        </Box>
      )}
    </Box>
  );
};
